/**
 * Utility functions for campaign preview functionality
 */

/**
 * Sample data for variable replacement in preview mode
 */
const PREVIEW_SAMPLE_DATA = {
  name: 'Sarah Martinez',
  child_name: 'Emma',
  child_age: '8',
  unsubscribe: '#unsubscribe',
  forward: '#forward'
};

/**
 * Replace MailerLite variables with sample data for preview purposes
 * @param content - HTML content with MailerLite variables
 * @returns Content with variables replaced with sample data
 */
export const replaceVariablesWithSampleData = (content: string): string => {
  if (!content) return content;

  let previewContent = content;

  // Replace each variable with sample data
  Object.entries(PREVIEW_SAMPLE_DATA).forEach(([variable, sampleValue]) => {
    // Replace {$variable} format (MailerLite format)
    const regex = new RegExp(`\\{\\$${variable}\\}`, 'gi');
    previewContent = previewContent.replace(regex, sampleValue);

    // Also replace {{variable}} format (alternate format)
    const altRegex = new RegExp(`\\{\\{${variable}\\}\\}`, 'gi');
    previewContent = previewContent.replace(altRegex, sampleValue);
  });

  return previewContent;
};

/**
 * Check if content contains any unreplaced variables
 * @param content - HTML content to check
 * @returns Array of unreplaced variables found
 */
export const findUnreplacedVariables = (content: string): string[] => {
  if (!content) return [];

  const variableMatches = content.match(/\{\$[^}]+\}/g) || [];
  const altVariableMatches = content.match(/\{\{[^}]+\}\}/g) || [];

  return [...variableMatches, ...altVariableMatches];
};

/**
 * Get sample data for a specific variable
 * @param variableName - Name of the variable (without braces)
 * @returns Sample value for the variable
 */
export const getSampleDataForVariable = (variableName: string): string => {
  const cleanVariableName = variableName.replace(/[{$}]/g, '').toLowerCase();
  return PREVIEW_SAMPLE_DATA[cleanVariableName as keyof typeof PREVIEW_SAMPLE_DATA] || `[${variableName}]`;
};

/**
 * Toggle between preview mode (with sample data) and edit mode (with variables)
 * @param content - HTML content
 * @param showPreview - Whether to show preview mode or edit mode
 * @returns Content with or without variable replacement
 */
export const togglePreviewMode = (content: string, showPreview: boolean): string => {
  return showPreview ? replaceVariablesWithSampleData(content) : content;
};